import React, { useState } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/store/store';
import { setGameState } from '@/store/gameStateSlice';
import { GameStateEnum } from '@/store/gameStateSlice';
import { updatePlayerResources } from '@/store/playerSlice';

// Styled components
const MenuContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const MenuContent = styled.div`
  background-color: #1a1a2e;
  border: 2px solid #4a4a82;
  border-radius: 10px;
  padding: 20px;
  width: 80%;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  color: white;
  box-shadow: 0 0 20px rgba(0, 0, 255, 0.3);
`;

const MenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #4a4a82;
`;

const Title = styled.h2`
  font-size: 24px;
  color: #00aaff;
  margin: 0;
`;

const ResourceDisplay = styled.div`
  display: flex;
  align-items: center;
  font-size: 18px;
  color: gold;
`;

const GoldIcon = styled.span`
  margin-right: 5px;
  font-size: 20px;
`;

const UpgradeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
`;

const CategoryTabs = styled.div`
  display: flex;
  margin-bottom: 15px;
  border-bottom: 1px solid #4a4a82;
`;

const CategoryTab = styled.button<{ active: boolean }>`
  background: ${props => props.active ? '#4a4a82' : 'transparent'};
  color: ${props => props.active ? 'white' : '#aaa'};
  border: none;
  padding: 8px 15px;
  cursor: pointer;
  font-size: 16px;
  border-radius: 5px 5px 0 0;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#4a4a82' : '#2a2a4a'};
  }
`;

const UpgradeCard = styled.div<{ disabled: boolean }>`
  background-color: ${props => props.disabled ? '#2a2a3e' : '#2a2a4a'};
  border: 1px solid ${props => props.disabled ? '#3a3a5e' : '#4a4a82'};
  border-radius: 8px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  opacity: ${props => props.disabled ? 0.6 : 1};
  transition: all 0.2s;
  
  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translateY(-3px)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 5px 15px rgba(0, 0, 255, 0.2)'};
  }
`;

const UpgradeTitle = styled.h3`
  font-size: 18px;
  margin: 0 0 10px 0;
  color: #00aaff;
`;

const UpgradeDescription = styled.p`
  font-size: 14px;
  color: #ccc;
  margin: 0 0 15px 0;
  flex-grow: 1;
`;

const UpgradeCost = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Cost = styled.span`
  color: gold;
  font-weight: bold;
`;

const PurchaseButton = styled.button<{ disabled: boolean }>`
  background-color: ${props => props.disabled ? '#444' : '#00aa44'};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.disabled ? '#444' : '#00cc55'};
  }
`;

const ContinueButton = styled.button`
  background-color: #00aaff;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 18px;
  cursor: pointer;
  margin-top: 20px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #0088cc;
  }
`;

// Upgrade types
export enum UpgradeCategory {
  PLAYER = 'Player',
  WEAPON = 'Weapon',
  DEFENSE = 'Defense',
  SPECIAL = 'Special'
}

interface Upgrade {
  id: string;
  name: string;
  description: string;
  cost: number;
  category: UpgradeCategory;
  effect: () => void;
}

// Sample upgrades
const sampleUpgrades: Upgrade[] = [
  {
    id: 'health_boost',
    name: 'Health Boost',
    description: 'Increase maximum health by 25 points',
    cost: 100,
    category: UpgradeCategory.PLAYER,
    effect: () => {
      // This would be implemented to increase max health
      console.log('Health boosted');
    }
  },
  {
    id: 'damage_boost',
    name: 'Damage Boost',
    description: 'Increase arrow damage by 15%',
    cost: 150,
    category: UpgradeCategory.WEAPON,
    effect: () => {
      // This would be implemented to increase damage
      console.log('Damage boosted');
    }
  },
  {
    id: 'fire_rate',
    name: 'Rapid Fire',
    description: 'Increase arrow firing rate by 10%',
    cost: 200,
    category: UpgradeCategory.WEAPON,
    effect: () => {
      // This would be implemented to increase fire rate
      console.log('Fire rate increased');
    }
  },
  {
    id: 'armor',
    name: 'Armor Plating',
    description: 'Reduce damage taken by 10%',
    cost: 250,
    category: UpgradeCategory.DEFENSE,
    effect: () => {
      // This would be implemented to reduce damage
      console.log('Armor added');
    }
  },
  {
    id: 'multi_shot',
    name: 'Multi-Shot',
    description: 'Chance to fire an additional arrow',
    cost: 300,
    category: UpgradeCategory.SPECIAL,
    effect: () => {
      // This would be implemented to add multi-shot
      console.log('Multi-shot enabled');
    }
  },
  {
    id: 'poison_arrows',
    name: 'Poison Arrows',
    description: 'Arrows have a chance to poison enemies',
    cost: 350,
    category: UpgradeCategory.SPECIAL,
    effect: () => {
      // This would be implemented to add poison effect
      console.log('Poison arrows enabled');
    }
  }
];

interface UpgradeMenuProps {
  gameController: any; // Replace with proper type
}

const UpgradeMenu: React.FC<UpgradeMenuProps> = ({ gameController }) => {
  const dispatch = useDispatch();
  const { resources } = useSelector((state: RootState) => state.player);
  const [selectedCategory, setSelectedCategory] = useState<UpgradeCategory>(UpgradeCategory.PLAYER);
  const [purchasedUpgrades, setPurchasedUpgrades] = useState<string[]>([]);
  
  // Filter upgrades by selected category
  const filteredUpgrades = sampleUpgrades.filter(upgrade => upgrade.category === selectedCategory);
  
  // Handle purchase
  const handlePurchase = (upgrade: Upgrade) => {
    if (resources.gold >= upgrade.cost && !purchasedUpgrades.includes(upgrade.id)) {
      // Apply the upgrade effect
      upgrade.effect();
      
      // Update gold
      dispatch(updatePlayerResources({
        gold: resources.gold - upgrade.cost
      }));
      
      // Mark as purchased
      setPurchasedUpgrades([...purchasedUpgrades, upgrade.id]);
    }
  };
  
  // Handle continue to next wave
  const handleContinue = () => {
    // Set game state back to WAVE_IN_PROGRESS
    dispatch(setGameState(GameStateEnum.WAVE_IN_PROGRESS));
    
    // Start the next wave
    if (gameController) {
      gameController.continueToNextWave();
    }
  };
  
  return (
    <MenuContainer>
      <MenuContent>
        <MenuHeader>
          <Title>Wave Completed - Upgrade Shop</Title>
          <ResourceDisplay>
            <GoldIcon>💰</GoldIcon>
            {resources.gold} Gold
          </ResourceDisplay>
        </MenuHeader>
        
        <CategoryTabs>
          {Object.values(UpgradeCategory).map(category => (
            <CategoryTab 
              key={category}
              active={category === selectedCategory}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </CategoryTab>
          ))}
        </CategoryTabs>
        
        <UpgradeGrid>
          {filteredUpgrades.map(upgrade => {
            const isPurchased = purchasedUpgrades.includes(upgrade.id);
            const canAfford = resources.gold >= upgrade.cost;
            const isDisabled = isPurchased || !canAfford;
            
            return (
              <UpgradeCard key={upgrade.id} disabled={isDisabled}>
                <UpgradeTitle>{upgrade.name}</UpgradeTitle>
                <UpgradeDescription>{upgrade.description}</UpgradeDescription>
                <UpgradeCost>
                  <Cost>{upgrade.cost} Gold</Cost>
                  <PurchaseButton 
                    disabled={isDisabled}
                    onClick={() => handlePurchase(upgrade)}
                  >
                    {isPurchased ? 'Purchased' : 'Buy'}
                  </PurchaseButton>
                </UpgradeCost>
              </UpgradeCard>
            );
          })}
        </UpgradeGrid>
        
        <ContinueButton onClick={handleContinue}>
          Continue to Next Wave
        </ContinueButton>
      </MenuContent>
    </MenuContainer>
  );
};

export default UpgradeMenu; 